import { generateGuid, randomString } from '../../../utilities/randomUtilities';
import { FONT, FONT_SOURCE, TEXT_ANCHOR_POINTS } from './Preview/PreviewDisplayConstants';

export const DEFAULT_DRIFT_SETTINGS = {
    enabled: false,
    intensity: 30,
    customMode: false,
    amplitudeX: 5,
    amplitudeY: 5,
    rotation: 0.5,
    speed: 1.5,
    octaves: 1,
    scale: 0,
    acceleration: 0
};

export const DEFAULT_LAYER_SPIN_SETTINGS = {
    enabled: false,
    speed: 0,
    acceleration: 0,
};

export const DEFAULT_LAYER_GLOW_SETTINGS = {
    enabled: false,
    glowType: "outer",
    blur: 3.6,
    scale: 1
};

export const DEFAULT_LAYER_FIRE_SETTINGS = {
    enabled: false,
    intensity: 5,
    detail: 1
};

export const DEFAULT_LAYER_DROP_SHADOW_SETTINGS = {
    enabled: false,
    blur: 5,
    opacity: 1,
    color: "0x000000"
};

export const DEFAULT_CUSTOM_LAYER_SETTINGS = {
    rotation: 0,
    waveType: "bass spectrum",
    reflectionType: "vertical",
    enabled: false,
    smoothed: true,
    inverted: false,
    waveStyle: "solid",
    pointRadius: 1,
    barWidth: 0.75,
    waveScaleFactor: 1.2,
    barCount: 200,
    movementDirection: "outward",
    hollowCenter: false,
    cutout: false,
    isInitialized: false,
    glowSettings: DEFAULT_LAYER_GLOW_SETTINGS,
    fireSettings: DEFAULT_LAYER_FIRE_SETTINGS,
    dropShadowSettings: DEFAULT_LAYER_DROP_SHADOW_SETTINGS,
    spinSettings: DEFAULT_LAYER_SPIN_SETTINGS
};

export const DEFAULT_PARTICLES_FLICKERING_SETTINGS = {
    enabled: false,
    reactive: false,
    minIntensity: 0.2199,
    maxIntensity: 0.3142,
    flickerSpeedUpFactor: 5
};

export const DEFAULT_PARTICLES_ROTATION_SETTINGS = {
    enabled: false,
    minSpeed: 0.002,
    maxSpeed: 0.03
};

export const MAX_IMAGE_ELEMENTS = 10;

// The Elements panel is split in two: everything that is not a picture, and the pictures.
export const ELEMENTS_PANEL_TABS = {
    OTHER: 0,
    IMAGE_ELEMENTS: 1,
    IMAGES: 1
};

// Scales the size the image is fitted to, both axes at once, so the picture always keeps its own
// proportions and the Width and Height controls only make it larger or smaller together.
export const DEFAULT_IMAGE_ELEMENT_SIZE_FACTOR = 1;
export const DEFAULT_IMAGE_SIZE_FACTOR = DEFAULT_IMAGE_ELEMENT_SIZE_FACTOR;

// A picture is fitted into a box 40% of the frame height, so filling the whole width of a landscape
// frame already needs a factor of 1 / 0.4 * 16 / 9, which is about 4.45. The ceiling leaves a little
// room above that, and the panel keeps a picture inside the frame anyway.
export const MAX_IMAGE_ELEMENT_SIZE_FACTOR = 5;
export const MAX_IMAGE_SIZE_FACTOR = MAX_IMAGE_ELEMENT_SIZE_FACTOR;

// The size controls read as a share of the frame, and a picture is never asked to be wider or taller
// than the frame it sits in.
export const MIN_IMAGE_ELEMENT_FRAME_PERCENTAGE = 1;
export const MIN_IMAGE_FRAME_PERCENTAGE = MIN_IMAGE_ELEMENT_FRAME_PERCENTAGE;
export const MAX_IMAGE_ELEMENT_FRAME_PERCENTAGE = 100;
export const MAX_IMAGE_FRAME_PERCENTAGE = MAX_IMAGE_ELEMENT_FRAME_PERCENTAGE;

// Blur and distance are base units, opacity is a fraction and angle is degrees. The Blur and Distance
// sliders show these five times over, so both defaults read 10 on a scale that runs to 100.
export const DEFAULT_IMAGE_ELEMENT_SHADOW_SETTINGS = {
    enabled: false,
    color: "0x000000",
    blur: 2,
    opacity: 0.5,
    distance: 2,
    angle: 45
};
export const DEFAULT_IMAGE_SHADOW_SETTINGS = DEFAULT_IMAGE_ELEMENT_SHADOW_SETTINGS;

// What the Blur and Distance sliders multiply the stored value by to get the number on screen. Both
// run to 100 on screen, so the stored value tops out at 20, which is the ceiling the backend keeps in
// MediaConstants.MaxImageElementDropShadowBlur and MaxImageElementDropShadowDistance.
export const IMAGE_ELEMENT_SHADOW_SLIDER_MULTIPLIER = 5;
export const IMAGE_SHADOW_SLIDER_MULTIPLIER = IMAGE_ELEMENT_SHADOW_SLIDER_MULTIPLIER;

export const PARTICLE_COUNT_MULTIPLIER = 20;
export const MAX_TOTAL_PARTICLE_COUNT = 2500;

export const DEFAULT_FLOCK_SETTINGS = {
    separation: 1.6,
    alignment: 1.0,
    cohesion: 1.0,
    vision: 70,
    speed: 1.42,
    turnSpeed: 0.028
};

// Raw value each flock slider spans, from its 0 position to its 100 position.
export const FLOCK_SETTINGS_RANGES = {
    separation: { min: 0, max: 4 },
    alignment: { min: 0, max: 4 },
    cohesion: { min: 0, max: 4 },
    vision: { min: 20, max: 120 },
    speed: { min: 0.04, max: 4 },
    turnSpeed: { min: 0.01, max: 0.1 }
};

export default {
    DEFAULT_VIDEO_SETTINGS: {
        "id": null,
        "version": "27",
        "presetId": "4e3e25ed-707d-4999-ae97-40daec60c817",
        "export": null,
        "thumbnailUrl": null,
        "aspectRatio": 0,
        "emojiImages": [],
        "visualizer": {
            "logoVisible": true,
            "waveCircles": [
                {
                    "id": generateGuid(),
                    "fillColor": "0xef5350",
                    "lineColor": "0xffffff",
                    "fillAlpha": 1,
                    "lineWidth": 0,
                    "lineAlpha": 1,
                    "secondaryFillColor": null,
                    "secondaryLineColor": null,
                    "secondaryFillAlpha": 1,
                    "secondaryLineAlpha": 1,
                    "visible": true,
                    "customOptions": {
                        ...DEFAULT_CUSTOM_LAYER_SETTINGS
                    }
                },
                {
                    "id": generateGuid(),
                    "fillColor": "0xffffff",
                    "lineColor": "0x000000",
                    "fillAlpha": 1,
                    "lineWidth": 0,
                    "lineAlpha": 1,
                    "secondaryFillColor": null,
                    "secondaryLineColor": null,
                    "secondaryFillAlpha": 1,
                    "secondaryLineAlpha": 1,
                    "visible": true,
                    "customOptions": {
                        ...DEFAULT_CUSTOM_LAYER_SETTINGS
                    }
                }
            ],
            "waveType": "bass spectrum",
            "layoutType": "webbed",
            "reflectionType": "vertical",
            "mediaSource": {
                "mediaId": null,
                "data": {
                    "url": "https://specterr.b-cdn.net/specterr-visualizer-image-b.png"
                },
                "mediaSourceType": 3,
                "mediaResourceType": 0,
                "operationStamp": randomString(),
                "isPreset": true
            },
            "shakeAmount": "little",
            "shakeIntensity": 20,
            "drift": DEFAULT_DRIFT_SETTINGS,
            "radiusFactor": 80,
            "seperationFactor": 0.8,
            "waveScaleFactor": 1.2,
            "logoSizeFactor": 0.95,
            "bounceFactor": 1,
            "delayed": true,
            "inverted": false,
            "waveStyle": "solid",
            "movementDirection": "outward",
            "barCount": 200,
            "barWidth": 0.75,
            "hollowCenter": false,
            "pointRadius": 1,
            "smoothed": true,
            "position": {
                "x": 0,
                "y": 0
            },
            "spinSettings": {
                "enabled": false,
                "speed": 0,
                "acceleration": 0,
                "logoLocked": false
            },
            "glowSettings": {
                "enabled": true,
                "glowType": "outer",
                "blur": 3.6,
                "scale": 1
            },
            "logoGlowSettings": {
                "enabled": false,
                "glowType": "inner",
                "blur": 3.6,
                "scale": 1
            },
            "fireSettings": {
                "enabled": false,
                "intensity": 5,
                "detail": 1
            },
            "dropShadowSettings": {
                "enabled": false,
                "blur": 5,
                "opacity": 1,
                "color": "0x000000"
            },
            "width": 888,
            "baseHeight": 5,
            "rotation": 0,
            "shape": "circle"
        },
        "background": {
            "mediaSource": {
                "mediaId": null,
                "data": {
                    "url": "https://specterr.b-cdn.net/bg-default.jpg"
                },
                "mediaSourceType": 3,
                "mediaResourceType": 0,
                "operationStamp": randomString(),
                "isPreset": true
            },
            "shakeEnabled": true,
            "shakeIntensity": 20,
            "motionBlurEnabled": true,
            "motionBlurIntensity": 20,
            "filterEnabled": true,
            "rotation": 0,
            "reflection": {
                "type": 0,
                "direction": 1,
            },
            "hlsAdjustment": {
                "enabled": false,
                "colorize": false,
                "alpha": 1,
                "hue": 0,
                "lightness": 0,
                "saturation": 0
            },
            "drift": DEFAULT_DRIFT_SETTINGS,
            "videoAcceleration": {
                "enabled": false,
                "intensity": 50
            },
            "slowZoom": {
                "enabled": false,
                "intensity": 30
            }
        },
        "text": {
            "textElements": [
                {
                    "text": "ARTIST NAME",
                    "color": "0xffffff",
                    "font": "Montserrat",
                    "fontSource": FONT_SOURCE.SYSTEM,
                    "fontSize": 52,
                    "bold": true,
                    "drift": DEFAULT_DRIFT_SETTINGS,
                    "position": {
                        "x": 0,
                        "y": 170
                    },
                    "anchorPoint": "CENTER",
                    "glowSettings": {
                        "enabled": false,
                        "blur": 15,
                        "scale": 2
                    },
                    "shadowSettings": {
                        "enabled": true,
                        "color": "0x000000",
                        "blur": 4,
                        "opacity": 1,
                        "distance": 4,
                        "angle": 30
                    }
                },
                {
                    "text": "TRACK NAME",
                    "color": "0xffffff",
                    "font": "Montserrat",
                    "fontSource": FONT_SOURCE.SYSTEM,
                    "fontSize": 45,
                    "bold": false,
                    "drift": DEFAULT_DRIFT_SETTINGS,
                    "position": {
                        "x": 0,
                        "y": 205
                    },
                    "anchorPoint": "CENTER",
                    "glowSettings": {
                        "enabled": false,
                        "blur": 15,
                        "scale": 2
                    },
                    "shadowSettings": {
                        "enabled": true,
                        "color": "0x000000",
                        "blur": 4,
                        "opacity": 1,
                        "distance": 4,
                        "angle": 30
                    }
                }
            ]
        },
        "lyrics": {
            "style": {
                "color": "0xffffff",
                "font": "Montserrat",
                "fontSource": FONT_SOURCE.SYSTEM,
                "fontSize": 52,
                "bold": true,
                "anchorPoint": "CENTER",
                "position": {
                    "x": 0,
                    "y": -160
                },
                "shadowSettings": {
                    "enabled": true,
                    "color": "0x000000",
                    "blur": 4,
                    "opacity": 1,
                    "distance": 4,
                    "angle": 30
                },
                "glowSettings": {
                    "enabled": false,
                    "blur": 15,
                    "scale": 2
                }
            },
            "segments": []
        },
        "elements": {
            "particles": {
                "direction": "OUT",
                "enabled": true,
                "speedUpEnabled": true,
                "flock": {
                    "customMode": false,
                    ...DEFAULT_FLOCK_SETTINGS
                },
                "items": [
                    {
                        "shape": "circle",
                        "color": "0xfbfbfb",
                        "maxOpacity": 1,
                        "maxSize": 0.3,
                        "minOpacity": 0.2,
                        "minSize": 0.1,
                        "mediaData": null,
                        "birthRate": 37.5,
                        "flickering": {
                            "enabled": false,
                            "reactive": false,
                            "minIntensity": 0.2199,
                            "maxIntensity": 0.3142
                        },
                        "rotationEnabled": false
                    }
                ]
            },
            "youTubeCta": {
                "enabled": false,
                "cornerPosition": 5
            },
            "imageElements": [],
            "images": []
        }
    },
    DEFAULT_AUDIO_SETTINGS: {
        url: null,
        displayFileName: null,
        startTime: 0,
        endTime: 0,
        bassMaxMagnitude: 0,
        wideMaxMagnitude: 0
    },
    DEFAULT_MAX_VALUES: {
        "maxLineWidth": 20.0,
        "maxRadiusFactor": 2.0,
        "maxSeperationFactor": 2.0,
        "maxWaveScaleFactor": 4.0,
        "maxLogoSizeFactor": 1.0,
        "maxBounceFactor": 5.0,
        "maxTextSizeFactor": 2.0,
        "maxBarCount": 200,
        "minBarCount": 4,
        "maxBarWidth": 1.0,
        "maxPointRadius": 20.0,
        "maxGlowBlur": 20.0,
        "maxGlowScale": 10.0,
        "maxFireIntensity": 10.0,
        "maxFireDetail": 5.0,
        "maxDropShadowBlur": 10.0,
        "maxDropShadowOpacity": 10.0,
        "maxDropShadowDistance": 20.0,
        "fetched": false
    },
    SETTINGS_PANELS: {
        GENERAL: 'general',
        AUDIO: 'audio',
        VISUALIZER: 'visualizer',
        BACKDROP: 'backdrop',
        TEXT: 'text',
        LYRICS: 'lyrics',
        ELEMENTS: 'elements',
        FEATURES: 'features'
    },
    // Index of each tab inside the Visualizer settings panel (order of the <Tab> elements
    // in SettingsPanelSectionVisualizer). Used by the Features panel deep links.
    VISUALIZER_SUB_TABS: {
        LAYERS: 0,
        SHAPE: 1,
        MOTION: 2,
        EFFECTS: 3
    },
    SHOWED_WALKTHROUGH_STORAGE_KEY: 'SHOW_WALKTHROUGH_STORAGE_KEY',
    SHOW_ADVANCED_SETTINGS_STORAGE_KEY: 'SHOW_ADVANCED_SETTINGS_STORAGE_KEY',
    SHOWED_UPDATE_DIALOG_STORAGE_KEY: 'SHOW_UPDATE_DIALOG_STORAGE_KEY',
    CREATE_MOD_EXPORT: 'e',
    CREATE_MOD_PRESET: 'p',
    DEFAULT_ACTIVE_EXPORT: {
        id: null,
        step: '',
        progress: null
    },
    LIMITED_VISIBILITY_WARNING_TEXT: 'Due to your browser settings, this effect may not be fully visible in the preview. Your final export will still include this effect even if it is not reflected in the preview. You can attempt to enable "Hardware Acceleration" in your browser settings to make this warning go away.',
    LYRICS_PANEL_TABS: {
        TEXT: 0,
        STYLES: 1
    },
    LYRICS_TIMELINE: {
        DEFAULT_SEGMENT_DURATION: 1,
        DEFAULT_SEGMENT_DISTANCE: 0.5,
        DEFAULT_SEGMENT_TEXT: '',
        SEGMENT_TEXT_MAX_LENGTH: 1000,
        SCALE_FACTOR: 50,
        HOTKEYS_SEGMENT_MOVE_STEP: 0.1,
        HOTKEYS_SEGMENT_RESIZE_STEP: 0.1,
        DRAGGING_CURSOR_TOP_SAFE_SHIFT: 8,
        DRAGGING_CURSOR_BOTTOM_SAFE_SHIFT: 4
    },
    SUPPORTED_LYRICS_FILE_EXTENSIONS: ["srt"],
    FONT_OPTIONS: [
        { value: FONT.AUDIOWIDE, text: FONT.AUDIOWIDE, font: FONT.AUDIOWIDE },
        { value: FONT.CREEPSTER, text: FONT.CREEPSTER, font: FONT.CREEPSTER },
        { value: FONT.DANCING_SCRIPT, text: 'Dancing Script', font: FONT.DANCING_SCRIPT },
        { value: FONT.EATER, text: FONT.EATER, font: FONT.EATER },
        { value: FONT.FREDERICKA_THE_GREAT, text: 'Fredericka the Great', font: FONT.FREDERICKA_THE_GREAT },
        { value: FONT.INDIE_FLOWER, text: 'Indie Flower', font: FONT.INDIE_FLOWER },
        { value: FONT.LIBRE_BARCODE_39_TEXT, text: 'Libre Barcode 39 Text', font: FONT.LIBRE_BARCODE_39_TEXT },
        { value: FONT.ORBITRON, text: FONT.ORBITRON, font: FONT.ORBITRON },
        { value: FONT.MERRIWEATHER, text: FONT.MERRIWEATHER, font: FONT.MERRIWEATHER },
        { value: FONT.MODAK, text: FONT.MODAK, font: FONT.MODAK },
        { value: FONT.MONTSERRAT, text: FONT.MONTSERRAT, font: FONT.MONTSERRAT },
        { value: FONT.NOSIFER, text: FONT.NOSIFER, font: FONT.NOSIFER },
        { value: FONT.OSWALD, text: FONT.OSWALD, font: FONT.OSWALD },
        { value: FONT.PACIFICO, text: FONT.PACIFICO, font: FONT.PACIFICO },
        { value: FONT.PERMANENT_MARKER, text: 'Permanent Marker', font: FONT.PERMANENT_MARKER },
        { value: FONT.PRESS_START_2P, text: 'Press Start 2P', font: FONT.PRESS_START_2P },
        { value: FONT.ROBOTO, text: FONT.ROBOTO, font: FONT.ROBOTO },
        { value: FONT.RUBIK_MONO_ONE, text: 'Rubik Mono One', font: FONT.RUBIK_MONO_ONE },
    ],
    ANCHOR_POINT_OPTIONS: [
        { value: TEXT_ANCHOR_POINTS.LEFT, text: 'Left' },
        { value: TEXT_ANCHOR_POINTS.CENTER, text: 'Center' },
        { value: TEXT_ANCHOR_POINTS.RIGHT, text: 'Right' },
    ],
    LYRICS_NEW_BADGE_TIMEOUT: 'February 1, 2021 12:00:00',
    RECOMMENDED_VIDEO_BACKGROUND_DURATION: 30,
    AUTOMATED_ANALYSIS_AUDIO_DURATION_LIMIT: 600
}

// Important! There is the same enumeration on back-end
export const MEDIA_SOURCE_TYPE = {
    STOCK_IMAGE: 0,
    USER_ASSET: 1,
    UPLOADED: 2,
    SPECTERR_CDN: 3,
    VIMEO: 4,
    STOCK_VIDEO: 6
};

// Important! There is the same enumeration on back-end
export const MEDIA_USAGE_TYPE = {
    LOGO: 0,
    BACKGROUND: 1,
    THUMBNAIL: 2,
    PARTICLE: 3,
    IMAGE_ELEMENT: 4
};

// Important! There is the same enumeration on back-end
export const MEDIA_RESOURCE_TYPE = {
    IMAGE: 0,
    VIDEO: 1,
    COLOR: 2,
    GRADIENT: 3
};

// Gradient style stored in the background mediaSource `gradientSettings.type`.
// Must stay in sync with the renderer's GRADIENT_TYPE constant. The mapper
// normalizes these to the SDK's capitalized "Linear"/"Radial" before rendering.
export const GRADIENT_TYPE = {
    LINEAR: 'linear',
    RADIAL: 'radial'
};

// Default values for a color/gradient background. Backgrounds now persist their
// settings as strongly typed `colorSettings` ({ color }) and `gradientSettings`
// ({ colors, type, angle, radius }) objects instead of the legacy `data` dictionary.
export const BACKGROUND_COLOR_DEFAULTS = {
    color: '#1e88e5',
    color2: '#000000',
    gradientType: GRADIENT_TYPE.LINEAR,
    gradientAngle: 90,
    gradientRadius: 100
};

// The stock image a video falls back to when no custom background is selected.
export const DEFAULT_BACKGROUND_IMAGE_URL = "https://specterr.b-cdn.net/bg-default.jpg";

export const VIDEO_RESOLUTIONS = {
    k4: {
        width: 3840,
        height: 2160
    },
    p1080: {
        width: 1920,
        height: 1080
    },
    p720: {
        width: 1280,
        height: 720
    },
    p540: {
        width: 960,
        height: 540
    },
    p360: {
        width: 640,
        height: 360
    }
};

export const ASPECT_RATIO = {
    LANDSCAPE: 0,
    PORTRAIT: 1,
    SQUARE: 2
};

export const AUDIO_UPLOAD_SUPPORTED_FORMATS = [
    'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', "audio/aac", "audio/vnd.dlna.adts", "audio/x-aac", ".aac"
];

export const IMAGE_UPLOAD_RESTRICTION = {
    MIN_WIDTH: 30,
    MIN_HEIGHT: 20,
    MAX_WIDTH: 8000,
    MAX_HEIGHT: 8000
}

export const PARTICLE_IMAGE_RULES = {
    ALLOWED_TYPES: ['image/png', 'image/jpeg', 'image/webp'],
    MAX_SIZE_KB: 500,
    MIN_DIMENSION_PX: 64,
    MAX_DIMENSION_PX: 2160
};

export const AUDIO_UPLOAD_RESTRICTION = {
    FREE: {
        MIN_SIZE: 0,                                        // Megabytes
        MAX_SIZE: 250,                                      // Megabytes
        MIN_DURATION: 1,                                    // Seconds
        MAX_DURATION: 36000,                                // Seconds
        AUDIO_FORMATS: AUDIO_UPLOAD_SUPPORTED_FORMATS       // Array of MIME type.
    },
    PREMIUM: {
        MIN_SIZE: 0,                                        // Megabytes
        MAX_SIZE: 500,                                      // Megabytes
        MIN_DURATION: 1,                                    // Seconds
        MAX_DURATION: 36000,                                // Seconds
        AUDIO_FORMATS: AUDIO_UPLOAD_SUPPORTED_FORMATS       // Array of MIME type.
    }
};

export const DEFAULT_AUDIO_TRACK = {
    SILENT_URL: 'https://vizflystorage.blob.core.windows.net/public/silence.mp3',
    SOUND_URL: 'https://vizflystorage.blob.core.windows.net/public/vizfly-sample.mp3',
    DURATION: 12.16,
    BASS_MAX_MAGNITUDE: 210.342,
    WIDE_MAX_MAGNITUDE: 210.342,
    BASS_FREQUENCIES_FILE_PATH: 'audio-data/default-bass-frequencies.json',
    WIDE_FREQUENCIES_FILE_PATH: 'audio-data/default-wide-frequencies.json',
    PREDEFINED_BASS_FREQUENCIES_FPS: 60,
    PREDEFINED_WIDE_FREQUENCIES_FPS: 60
};

export const VIDEO_SAVING_STEP_STATUS = {
    PENDING: 'pending',
    UNCHANGED: 'unchanged',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    FAILED: 'failed',
    CANCELLED: 'cancelled'
};

export const MAX_PARTICLES = 5;

export const PARTICLE_SHAPE_TYPES = {
    CIRCLE: 'circle',
    HEART: 'heart',
    STAR: 'star',
    NOTE: 'note',
    EMOJI: 'emoji',
    CUSTOM: 'custom'
};

export const SKIN_TONE_MODIFIERS = {
    LIGHT: '1f3fb',
    MEDIUM_LIGHT: '1f3fc',
    MEDIUM: '1f3fd',
    MEDIUM_DARK: '1f3fe',
    DARK: '1f3ff'
};

// How picked assets reach blob storage: lazy keeps them local until the project is
// saved, direct uploads them the moment they are submitted (upload-and-switch).
export const ASSET_UPLOADING_MODE = {
    LAZY: 'lazy',
    DIRECT: 'direct'
};